import ElasticsearchAPIConnector, { type IApiClientTransporter, type ResponseBody, type SearchRequest } from "@elastic/search-ui-elasticsearch-connector";
import type { SearchDriverOptions } from "@elastic/search-ui";
import type { ApiService } from "../../services/apiService";
import { stateToUrl, urlToState } from "./urlState";
import { searchWithCompleteUspFacets, USP_FACET_FIELDS } from "./uspFacets";
import { COMMERCIAL_UNIT_CODE_EXCLUDE_PATTERN } from "./productMetadata";

export function wrapNestedPackageFilters(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(wrapNestedPackageFilters);
  if (!value || typeof value !== "object") return value;
  const record = value as Record<string, unknown>;
  if ((record.term || record.terms || record.range) && JSON.stringify(record).includes('"packages.')) {
    return { nested: { path: "packages", query: record } };
  }
  return Object.fromEntries(Object.entries(record).map(([key, child]) => [key, key === "nested" ? child : wrapNestedPackageFilters(child)]));
}

// CVL fields contain an internal `key` and a readable `value`. Facets must use
// the keyword subfield of the value so Elasticsearch can aggregate exact labels
// without exposing internal inRiver IDs in the filter pane.
export const VALUE_FACET_FIELDS = [
  ...USP_FACET_FIELDS,
  "ItemBrand.value.keyword",
  "ItemSubBrandList.value.keyword",
  "ItemProductCategory.value.keyword",
  "ItemProductGroup.value.keyword",
  "ItemProductSubGroup.value.keyword",
  "ItemSoldState.value.keyword",
  "ItemTemperatureCondition.value.keyword",
  "ItemProductPreparation.value.keyword",
  "ItemCommercialUnit.value.keyword",
  "ItemLUOwner.value.keyword",
  "packages.PackageUnitDescriptor.value.keyword",
] as const;

export function wrapNestedPackageAggregations(
  aggregations: Record<string, unknown>,
  parentPath: string[] = [],
): string[][] {
  const nestedPaths: string[][] = [];
  for (const [key, value] of Object.entries(aggregations)) {
    if (!value || typeof value !== "object") continue;
    const aggregation = value as Record<string, unknown>;
    const terms = aggregation.terms as Record<string, unknown> | undefined;
    if (typeof terms?.field === "string" && terms.field.startsWith("packages.")) {
      aggregations[key] = { nested: { path: "packages" }, aggs: { inner: aggregation } };
      nestedPaths.push([...parentPath, key]);
      continue;
    }
    const children = (aggregation.aggs ?? aggregation.aggregations) as Record<string, unknown> | undefined;
    if (children) nestedPaths.push(...wrapNestedPackageAggregations(children, [...parentPath, key]));
    if (aggregation.filter) aggregation.filter = wrapNestedPackageFilters(aggregation.filter);
  }
  return nestedPaths;
}

export function unwrapNestedPackageAggregations(aggregations: Record<string, unknown>, paths: string[][]): void {
  for (const path of paths) {
    let parent = aggregations;
    for (const segment of path.slice(0, -1)) {
      const child = parent[segment];
      if (!child || typeof child !== "object") { parent = {}; break; }
      parent = child as Record<string, unknown>;
    }
    const key = path.at(-1);
    const wrapped = key ? parent[key] as Record<string, unknown> | undefined : undefined;
    if (key && wrapped?.inner && typeof wrapped.inner === "object") parent[key] = wrapped.inner;
  }
}

class AuthenticatedTransporter implements IApiClientTransporter {
  headers = {};
  private readonly api: ApiService;
  constructor(api: ApiService) { this.api = api; }
  performRequest(request: SearchRequest): Promise<ResponseBody> {
    const { index, ...body } = request;
    if (index && index !== "product-search") throw new Error("Unexpected product search index.");
    return this.api.post<ResponseBody>("/product-search/query", body);
  }
}

export function buildProductSearchConfig(api: ApiService): SearchDriverOptions {
  const connector = new ElasticsearchAPIConnector({
    index: "product-search",
    apiClient: new AuthenticatedTransporter(api),
    getQueryFn: ({ searchTerm }) => searchTerm ? ({
      bool: { should: [
        { term: { "ItemNumber": { value: searchTerm, boost: 12 } } },
        { prefix: { "ItemNumber": { value: searchTerm, boost: 8 } } },
        { term: { "ItemGtin": { value: searchTerm, boost: 12 } } },
        { prefix: { "ItemGtin": { value: searchTerm, boost: 8 } } },
        { multi_match: { query: searchTerm, fields: ["ItemName^5", "ItemDescription.*^2", "ItemDescriptionSales.*^2", "ItemBrand.value^3", "ItemProductCategory.value^2"], type: "best_fields", fuzziness: "AUTO" } },
      ], minimum_should_match: 1 } } as never) : ({ match_all: {} }),
    interceptSearchRequest: async ({ requestBody }, next) => {
      // Search UI builds the standard request. The package field is nested in the index;
      // wrap its aggregation while preserving the connector's response key.
      const aggs = requestBody.aggregations ?? requestBody.aggs;
      if (requestBody.query) requestBody.query = wrapNestedPackageFilters(requestBody.query) as typeof requestBody.query;
      if (requestBody.post_filter) requestBody.post_filter = wrapNestedPackageFilters(requestBody.post_filter) as typeof requestBody.post_filter;
      const nestedPaths = aggs && typeof aggs === "object"
        ? wrapNestedPackageAggregations(aggs as Record<string, unknown>)
        : [];
      const response = await searchWithCompleteUspFacets(requestBody, next);
      const responseAggregations = response.aggregations as Record<string, unknown> | undefined;
      if (responseAggregations) unwrapNestedPackageAggregations(responseAggregations, nestedPaths);
      return response;
    },
  });

  const valueFacets = [...VALUE_FACET_FIELDS, "ItemIsPrivateLabel"];
  return {
    apiConnector: connector,
    alwaysSearchOnInitialLoad: true,
    initialState: { resultsPerPage: 20, ...urlToState(window.location.href) },
    searchQuery: {
      result_fields: {
        entityId: { raw: {} }, primaryImageUrl: { raw: {} }, ItemNumber: { raw: {} }, ItemGtin: { raw: {} }, ItemName: { raw: {} }, ItemDescription: { raw: {} },
        ItemDescriptionSales: { raw: {} }, ItemBrand: { raw: {} }, ItemProductCategory: { raw: {} }, ItemProductGroup: { raw: {} },
        ItemNetWeight: { raw: {} }, ItemPackWeight: { raw: {} }, ItemDietType: { raw: {} }, ItemSliced: { raw: {} }, ItemProductPreparation: { raw: {} },
        packages: { raw: {} },
      },
      search_fields: { ItemName: { weight: 5 }, "ItemDescription.en": { weight: 2 }, "ItemDescriptionSales.en": { weight: 2 }, "ItemBrand.value": { weight: 3 } },
      facets: {
        ...Object.fromEntries(valueFacets.map((field) => [field, {
          type: "value",
          size: 50,
          sort: "count",
          ...(field === "ItemCommercialUnit.value.keyword" ? { exclude: COMMERCIAL_UNIT_CODE_EXCLUDE_PATTERN } : {}),
        }])),
        ItemNetWeight: { type: "range", ranges: [{ from: 0, to: 50, name: "0–50 g" }, { from: 50, to: 100, name: "50–100 g" }, { from: 100, to: 250, name: "100–250 g" }, { from: 250, to: 500, name: "250–500 g" }, { from: 500, name: "500 g+" }] },
      },
      disjunctiveFacets: valueFacets,
    },
    trackUrlState: true,
    routingOptions: {
      readUrl: () => window.location.href,
      writeUrl: (url, { replaceUrl }) => {
        if (window.location.pathname === "/product-management/search") window.history[replaceUrl ? "replaceState" : "pushState"](window.history.state, "", url);
      },
      stateToUrl: (state) => stateToUrl(state, new URLSearchParams(window.location.search).get("view") ?? "list", new URLSearchParams(window.location.search).get("language") ?? "en"),
      urlToState,
      routeChangeHandler: (handler) => { const listener = () => { if (window.location.pathname === "/product-management/search") handler(window.location.href); }; window.addEventListener("popstate", listener); return () => window.removeEventListener("popstate", listener); },
    },
  };
}
